// Pages présentes dans le code mais pas encore publiables.
//
// Une page dont les données ne sont pas validées n'a rien à faire en
// production — ni servie, ni au sitemap, ni liée. Mais elle a tout à faire
// dans le dépôt : c'est là qu'elle se relit, se corrige et se déverrouille.
// Ce module est la SEULE source qui dise si une telle page est publiable ;
// le routeur, le pré-rendu, les liens et le contrôle de build la consultent.
//
// Ajouter une page ici plutôt que de commenter sa route : une route commentée
// n'est pas relue, une page verrouillée l'est.
import prix from './prix-demenagement.json';
import cartons from './cartons-demenagement.json';
import { HUB, HUB_CHANGEMENT_ADRESSE, OPERATEURS, cheminOperateur } from './changement-adresse';
import { PAGES_GARDE_MEUBLES, cheminGardeMeublesCommune } from './garde-meubles-communes';
import { PAGE as PAGE_CONGE, PAGE_CONGE_DEMENAGEMENT } from './conge-demenagement';
import { PAGE as PAGE_DEBARRAS_DONNEES, PAGE_DEBARRAS } from './debarras';
import { PAGES_USAGES, cheminUsage } from './garde-meubles-usages';
import { DESTINATIONS, SUPPORTS_INTERNATIONAL, cheminDestination, cheminSupport } from './destinations';

/** Page prix & méthode de devis — LOT 6 du plan SEO. */
export const PAGE_PRIX_DEMENAGEMENT = '/demenagement/prix-demenagement-liege';

/** Page cartons : combien, lesquels, où les trouver — LOT 13 du plan SEO. */
export const PAGE_CARTONS = '/demenagement/cartons-demenagement';

/**
 * Hub changement d'adresse — LOT 11 du plan SEO : le pilier, puis une fiche par
 * opérateur. Chaque fiche a son propre verrou dans changement-adresse.json.
 * Un opérateur sans page (`fiche: false`) n'est pas une page en préparation :
 * il n'est ni routé vers une fiche (OperateurAdressePage répond 404), ni
 * pré-rendu, ni lié — il n'a donc rien à faire ici.
 */
export const PAGE_CHANGEMENT_ADRESSE = HUB_CHANGEMENT_ADRESSE;

/** Congé de déménagement et droits du locataire — LOT 12, rattachée au hub. */
export { PAGE_CONGE_DEMENAGEMENT };
/** Débarras / vide-maison — LOT 9. Service à confirmer par Gramme avant toute publication. */
export { PAGE_DEBARRAS };

const PUBLIABLE: Record<string, boolean> = {
  [PAGE_PRIX_DEMENAGEMENT]: prix.publiable === true,
  [PAGE_CARTONS]: cartons.publiable === true,
  ...Object.fromEntries(OPERATEURS.filter((o) => o.fiche).map((o) => [cheminOperateur(o.slug), o.publiable === true])),
  [PAGE_CHANGEMENT_ADRESSE]: HUB.publiable === true,
  // Garde-meubles par commune (lot 7) : une page par vague, chacune son verrou.
  ...Object.fromEntries(PAGES_GARDE_MEUBLES.map((p) => [cheminGardeMeublesCommune(p.slug), p.publiable === true])),
  [PAGE_CONGE_DEMENAGEMENT]: PAGE_CONGE.publiable === true,
  [PAGE_DEBARRAS]: PAGE_DEBARRAS_DONNEES.publiable === true,
  // Garde-meubles, cas d'usage (lot 8) : trois pages, chacune son verrou.
  ...Object.fromEntries(PAGES_USAGES.map((p) => [cheminUsage(p.slug), p.publiable === true])),
  // Déménagement international (lot 17) : une page par pays, chacune son
  // verrou `published` en base — et le contrôle de build exige en plus la
  // relecture du dépôt (verifiePar) avant de la servir. Les trois pages
  // supports suivent le même mécanisme, dans le dépôt.
  ...Object.fromEntries(DESTINATIONS.map((d) => [cheminDestination(d.slug), d.published === true])),
  ...Object.fromEntries(SUPPORTS_INTERNATIONAL.map((s) => [cheminSupport(s.slug), s.publiable === true])),
};

/**
 * Une page inconnue de ce module est publiable : le verrou ne concerne que les
 * pages qui s'y déclarent, jamais le reste du site.
 */
export function estPubliable(chemin: string): boolean {
  return PUBLIABLE[chemin] ?? true;
}

/** Les chemins verrouillés, pour le pré-rendu et le contrôle de build. */
export const PAGES_VERROUILLEES: readonly string[] = Object.entries(PUBLIABLE)
  .filter(([, ok]) => !ok)
  .map(([chemin]) => chemin);
